import { useState } from "react";
import { Copy, Mail, MapPin, Phone, Clock, Check, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";

export default function Contact() {
    const { t } = useLang();
    const [copied, setCopied] = useState(null);

    const buildMailto = (email) =>
        `mailto:${email}?subject=${encodeURIComponent(t.contact.subjectLine)}&body=${encodeURIComponent(t.contact.mailBody)}`;

    const copy = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(key);
            setTimeout(() => setCopied(null), 1800);
        } catch { /* noop */ }
    };

    const emailBlocks = [
        { key: "general", label: t.contact.block1Title, email: t.contact.block1Email },
        { key: "exec", label: t.contact.block2Title, email: t.contact.block2Email },
    ];

    const info = [
        { icon: Phone, label: t.contact.phoneDELabel, value: CONTACT.phoneDE_Display, href: `tel:${CONTACT.phoneDE_Tel}`, testid: "contact-phone-de" },
        { icon: Phone, label: t.contact.phoneROLabel, value: CONTACT.phoneRO_Display, href: `tel:${CONTACT.phoneRO_Tel}`, testid: "contact-phone-ro" },
        { icon: MapPin, label: t.contact.addressLabel, value: CONTACT.addressFull, testid: "contact-address" },
        { icon: Clock, label: t.contact.hoursLabel, value: t.contact.hoursValue, testid: "contact-hours" },
    ];

    return (
        <main data-testid="page-contact" className="bg-white">
            <SEO pathKey="contact" path="/contact" />
            <section className="pt-28 lg:pt-36 pb-12 lg:pb-20 blueprint-bg">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-600">
                                {t.contact.kicker}
                            </div>
                            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter text-navy leading-[0.92]">
                                {t.contact.h1}
                            </h1>
                        </Reveal>
                    </div>
                    <Reveal delay={120} className="lg:col-span-4">
                        <p className="text-steel-600 leading-relaxed">{t.contact.intro}</p>
                    </Reveal>
                </div>
            </section>

            {/* Email blocks */}
            <section className="py-16 lg:py-20">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 border-t border-s border-steel-100">
                    {emailBlocks.map((b, i) => (
                        <Reveal
                            key={b.key}
                            delay={i * 100}
                            className="p-8 lg:p-10 border-e border-b border-steel-100 bg-white"
                        >
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-600">
                                {b.label}
                            </div>
                            <a
                                href={buildMailto(b.email)}
                                data-testid={`contact-email-${b.key}`}
                                className="block mt-4 font-display text-2xl lg:text-3xl text-navy tracking-tight link-underline break-all"
                            >
                                {b.email}
                            </a>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <a
                                    href={buildMailto(b.email)}
                                    data-testid={`contact-mailto-${b.key}`}
                                    className="inline-flex items-center gap-2 bg-navy text-white px-5 py-3 font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors"
                                >
                                    <Mail className="h-4 w-4" />
                                    {t.contact.ctaMail}
                                    <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                                </a>
                                <button
                                    onClick={() => copy(b.email, b.key)}
                                    data-testid={`contact-copy-${b.key}`}
                                    className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-3 font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-navy hover:text-white transition-colors"
                                >
                                    {copied === b.key ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    {copied === b.key ? t.contact.copied : t.contact.ctaCopy}
                                </button>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Phone + address tiles */}
            <section className="pb-16 lg:pb-24">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-s border-steel-100">
                        {info.map((it, i) => {
                            const Icon = it.icon;
                            const body = (
                                <div className="font-display text-xl tracking-tight text-navy mt-2">
                                    {it.value}
                                </div>
                            );
                            return (
                                <Reveal
                                    key={it.label}
                                    delay={i * 60}
                                    className="p-7 border-e border-b border-steel-100 bg-white"
                                >
                                    <div className="flex items-center gap-3 text-steel-600">
                                        <Icon className="h-4 w-4" />
                                        <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em]">
                                            {it.label}
                                        </span>
                                    </div>
                                    {it.href ? (
                                        <a href={it.href} data-testid={it.testid} className="block link-underline">
                                            {body}
                                        </a>
                                    ) : (
                                        <div data-testid={it.testid}>{body}</div>
                                    )}
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Inline functional contact form */}
            <section className="pb-24 lg:pb-32 bg-steel-50 border-t border-steel-100 pt-20 lg:pt-24">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <Reveal className="lg:col-span-5">
                        <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-navy">
                            {t.contact.kicker}
                        </div>
                        <h2 className="font-display mt-4 text-4xl lg:text-5xl uppercase tracking-tighter text-navy leading-[0.95]">
                            {t.contact.formTitle}
                        </h2>
                        <p className="mt-6 text-steel-800 leading-relaxed">{t.contact.formSub}</p>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-7">
                        <ContactForm />
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
